import { motion } from "framer-motion";
import { Code, Webhook, Database, Plug, BookOpen, KeyRound } from "lucide-react";

const ICONS = { database: Database, plug: Plug, webhook: Webhook, "key-round": KeyRound, code: Code, "book-open": BookOpen };

export const API_DOCS_DEFAULT_DATA = {
  hero: {
    eyebrow: "Developer Docs",
    title: "Connect Antera to the rest of your stack.",
    tagline: "Antera exposes REST and PromoStandards APIs so distributors, suppliers, and integrators can synchronize orders, inventory, pricing, and customer data with any system. This page lists the public capabilities and how to get access.",
  },
  capabilities: [
    { icon: "database", title: "Core Records", desc: "Read and write customers, contacts, quotes, orders, invoices, products, inventory, and payments." },
    { icon: "plug", title: "PromoStandards", desc: "Native PromoStandards endpoints for live pricing, inventory, order status, shipping, and invoicing." },
    { icon: "webhook", title: "Webhooks", desc: "Subscribe to order, invoice, shipment, and inventory events with retry-safe delivery." },
    { icon: "key-round", title: "Authentication", desc: "OAuth 2.0 / API key authentication with per-environment credentials and role-scoped access." },
  ],
  endpoints_heading: "Example endpoints",
  endpoints: [
    { method: "GET",    path: "/api/v1/orders" },
    { method: "POST",   path: "/api/v1/orders" },
    { method: "GET",    path: "/api/v1/orders/{id}" },
    { method: "PATCH",  path: "/api/v1/orders/{id}" },
    { method: "GET",    path: "/api/v1/inventory" },
    { method: "POST",   path: "/api/v1/webhooks" },
  ],
  access: {
    heading: "Request access to the full reference",
    body_prefix: "Antera customers and approved integrators receive credentials, the complete reference, and Postman collections directly from our team. Email ",
    email: "api@anterasoftware.com",
    body_suffix: " with your account name and integration use case to get started.",
  },
};

function MethodBadge({ method }) {
  const color = {
    GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
    POST: "bg-sky-50 text-sky-700 border-sky-200",
    PATCH: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-rose-50 text-rose-700 border-rose-200",
  }[method] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex justify-center min-w-[58px] text-[11px] font-mono font-semibold px-2 py-1 rounded border ${color}`}>
      {method}
    </span>
  );
}

export default function ApiDocsPage({ data }) {
  const d = { ...API_DOCS_DEFAULT_DATA, ...(data || {}) };
  const hero = { ...API_DOCS_DEFAULT_DATA.hero, ...(d.hero || {}) };
  const access = { ...API_DOCS_DEFAULT_DATA.access, ...(d.access || {}) };

  return (
    <div data-testid="api-docs-page" className="page-transition">
      <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {hero.eyebrow ? (
              <span className="inline-block text-xs font-semibold text-sky-600 tracking-widest uppercase bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 mb-5">
                {hero.eyebrow}
              </span>
            ) : null}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">{hero.title}</h1>
            <p className="mt-5 text-base lg:text-lg text-slate-600 leading-relaxed">{hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-12 lg:pb-16 bg-slate-50/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(d.capabilities || []).map((c, i) => {
              const Icon = ICONS[c.icon] || Database;
              return (
                <motion.div
                  key={c.title || i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  data-testid={`api-capability-${(c.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="bg-white border border-slate-200 rounded-xl p-5"
                >
                  <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">{c.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-12 lg:pb-16 bg-slate-50/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Code className="h-4 w-4 text-sky-600" />
              <h2 className="text-sm font-semibold text-slate-900">{d.endpoints_heading}</h2>
            </div>
            <ul className="divide-y divide-slate-100">
              {(d.endpoints || []).map((e, i) => (
                <li
                  key={`${e.method}-${e.path}-${i}`}
                  data-testid={`api-endpoint-${(e.method || "").toLowerCase()}-${(e.path || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="px-5 py-3 flex items-center gap-3"
                >
                  <MethodBadge method={e.method} />
                  <code className="text-sm font-mono text-slate-700 break-all">{e.path}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-20 bg-slate-50/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-xl p-6 lg:p-8 flex items-start gap-4">
            <BookOpen className="h-6 w-6 text-sky-600 shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-slate-900">{access.heading}</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                {access.body_prefix}
                <a href={`mailto:${access.email}`} className="text-sky-600 hover:text-sky-700 font-medium">
                  {access.email}
                </a>
                {access.body_suffix}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
